import { P2GBox, P2GCheckBoxLegacy, P2GGrid, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { useState, useEffect } from 'react';
import { Dimensions, PackageApiModel, RuleApiModel } from '~/lib/api/smartsend/types';
import { EventSections } from '../../../lib/events/EventLogger';
import { ToInlineCommaList } from '../../../lib/helpers/StringHelper';
import AlertWithIcon from '../../AlertWithIcon';
import { DimensionDisplay } from '../DimensionDisplay';
import { DimensionsSelection } from '../DimensionsSelection';
import {
  generateDefaultDimensionsRule,
  generateDefaultWeightRule,
  generateDimensionsActions,
  generateWeightActions,
  GetDimensionsFromRule,
  GetWeightFromRule,
  RuleNames
} from '../rules/RuleGenerator';
import { GetDefaultRuleByName } from '../rules/RuleLocator';
import { PanelButtons } from '../panels/PanelButtons';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

interface props {
  affected: number;
  showDefaultDimensions: boolean;
  showDefaultWeight: boolean;
  preSelect: boolean;
  confirm?: (dimensions: Dimensions) => void;
  cancel?: () => void;
  getPackages: () => Promise<PackageApiModel[]>;
  getRules: (type: 'SmartSend') => Promise<RuleApiModel[]>;
  createRule: (rule: RuleApiModel) => Promise<boolean>;
  updateRule: (rule: RuleApiModel) => Promise<boolean>;
}

export default function ApplyDimensions(props: props) {
  const [saveDimensionsRule, setSaveDimensionsRule] = useState(false);
  const [saveWeightRule, setSaveWeightRule] = useState(false);
  const [existingDimensionsRule, setExistingDimensionsRule] = useState<RuleApiModel>(null);
  const [existingWeightRule, setExistingWeightRule] = useState<RuleApiModel>(null);
  const [loading, setLoading] = useState(props.showDefaultDimensions || props.showDefaultWeight);
  const { track } = useAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const [dimensions, setDimensions] = useState<Dimensions>({
    Weight: 0,
    Height: 0,
    Width: 0,
    Length: 0
  });

  const changes = [
    dimensions.Weight > 0 && 'weight',
    dimensions.Height > 0 && 'height',
    dimensions.Width > 0 && 'width',
    dimensions.Length > 0 && 'length'
  ].filter((x) => !!x);

  const onSave = async () => {
    if (saveDimensionsRule) {
      if (existingDimensionsRule) {
        await props.updateRule({
          ...existingDimensionsRule,
          Actions: generateDimensionsActions(dimensions.Height, dimensions.Width, dimensions.Length)
        });
      } else {
        track({
          type: 'default_dimensions_rule_click',
          payload: {
            ...GenerateBaseAnalyticsPayload([
              EventSections.SmartSend,
              EventSections.BulkActionSideBar,
              EventSections.InlineRules
            ])
          }
        });

        await props.createRule(generateDefaultDimensionsRule(dimensions.Height, dimensions.Width, dimensions.Length));
      }
    }

    if (saveWeightRule) {
      if (existingWeightRule) {
        await props.updateRule({
          ...existingWeightRule,
          Actions: generateWeightActions(dimensions.Weight)
        });
      } else {
        track({
          type: 'default_weight_rule_click',
          payload: {
            ...GenerateBaseAnalyticsPayload([
              EventSections.SmartSend,
              EventSections.BulkActionSideBar,
              EventSections.InlineRules
            ])
          }
        });
        await props.createRule(generateDefaultWeightRule(dimensions.Weight));
      }
    }

    props.confirm(dimensions);
  };

  useEffect(() => {
    if (props.showDefaultDimensions || props.showDefaultWeight) {
      props
        .getRules('SmartSend')
        .then((r) => {
          const updatedDims = { ...dimensions };
          if (props.showDefaultDimensions) {
            const dRule = r.find((x) => GetDefaultRuleByName(x, RuleNames.Dimensions));
            if (dRule) {
              setExistingDimensionsRule(dRule);
              setSaveDimensionsRule(props.preSelect);
              const d = GetDimensionsFromRule(dRule);
              updatedDims.Height = d.Height;
              updatedDims.Width = d.Width;
              updatedDims.Length = d.Length;
            }
          }
          if (props.showDefaultWeight) {
            const wRule = r.find((x) => GetDefaultRuleByName(x, RuleNames.Weight));
            if (wRule) {
              setExistingWeightRule(wRule);
              setSaveWeightRule(props.preSelect);
              const w = GetWeightFromRule(wRule);
              updatedDims.Weight = w;
            }
          }
          setDimensions(updatedDims);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [props.showDefaultDimensions, props.showDefaultWeight]);

  const showDefaultDimensions =
    props.showDefaultDimensions && dimensions.Height > 0 && dimensions.Width > 0 && dimensions.Length > 0;

  const showDefaultWeight = props.showDefaultWeight && dimensions.Weight > 0;

  return (
    <P2GBox textAlign="center">
      <P2GBox textAlign="left">
        {loading ? (
          <P2GProgress />
        ) : (
          <DimensionsSelection
            dimensions={dimensions}
            onUpdate={setDimensions}
            getPackages={props.getPackages}
            startMinimised={false}
          />
        )}
      </P2GBox>
      {changes.length > 0 && (
        <P2GBox textAlign="left" mt={2}>
          <AlertWithIcon>
            <P2GGrid container spacing={1} direction="column">
              <P2GGrid item>
                {changes.length === 4 ? (
                  <P2GText component="p">
                    {t('DIMENSION_CHANGE_ALERT', { defaultValue: 'All dimensions and weight will be changed.' })}
                  </P2GText>
                ) : (
                  <P2GText component="p">
                    {t('DIMENSION_CHANGE_ALERT_ALTERNATIVE', {
                      defaultValue: 'Only {{values}} will be changed.',
                      values: ToInlineCommaList(changes)
                    })}
                  </P2GText>
                )}
              </P2GGrid>
            </P2GGrid>
          </AlertWithIcon>
        </P2GBox>
      )}
      <P2GBox textAlign="left" mt={showDefaultDimensions || showDefaultWeight ? 2 : 0}>
        {showDefaultDimensions && (
          <P2GCheckBoxLegacy
            id="SaveRule"
            onChange={setSaveDimensionsRule}
            checked={saveDimensionsRule}
            label={
              <P2GText>
                <Trans
                  i18nKey="SAVE_DIMENSIONS_RULE_INPUT_LABEL"
                  values={{ dimensions: DimensionDisplay(dimensions) }}
                  defaults="Apply <strong>{{dimensions}}</strong> when no dimensions are imported."
                />
              </P2GText>
            }
            labelPlacement="end"
            variant="primary"
            error={false}
          />
        )}
        {showDefaultWeight && (
          <P2GCheckBoxLegacy
            id="SaveWeightRule"
            onChange={setSaveWeightRule}
            checked={saveWeightRule}
            label={
              <P2GText>
                <Trans
                  i18nKey="SAVE_WEIGHT_RULE_INPUT_LABEL"
                  values={{ weight: dimensions.Weight }}
                  defaults="Apply <strong>{{weight}}kg</strong> when no weight is imported."
                />
              </P2GText>
            }
            labelPlacement="end"
            variant="primary"
            error={false}
          />
        )}
      </P2GBox>
      <P2GBox mt={2} />
      <PanelButtons
        onSave={props.confirm && onSave}
        saveDisabled={loading}
        onCancel={props.cancel}
        cancelText={t('CANCEL_BUTTON', { defaultValue: 'Cancel' })}
      />
    </P2GBox>
  );
}
